import { useEffect, useState } from "react";
import { Users, UserPlus, Eye, EyeOff, X, Check } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const pill = (on) => `text-xs font-semibold px-3 py-1.5 rounded-full transition ${on ? "bg-stone-900 text-white" : "bg-stone-100 text-stone-600 hover:bg-stone-200"}`;
const field = "text-sm rounded-xl border border-stone-300 px-3 py-2 bg-white focus:outline-none focus:border-amber-600";

function InviteForm({ onDone }) {
  const [f, setF] = useState({ handle: "", code: "", kind: "friend", tier: "garden" });
  const send = async () => {
    try {
      const r = await api.post("/connections/invite", { ...f, handle: f.handle || null, code: f.code || null });
      toast.success(`Invite sent to ${r.data.other_handle}.`); setF({ handle: "", code: "", kind: "friend", tier: "garden" }); onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't send that invite"); }
  };
  return (
    <div className="warm-card p-5 space-y-3" data-testid="invite-form">
      <div className="flex items-center gap-2 font-display font-bold text-stone-900"><UserPlus className="w-4 h-4" /> Invite someone</div>
      <div className="grid gap-2 sm:grid-cols-2">
        <input className={field} placeholder="Their community handle" value={f.handle} onChange={(e) => setF({ ...f, handle: e.target.value })} data-testid="invite-handle" />
        <input className={field} placeholder="…or their connect code" value={f.code} onChange={(e) => setF({ ...f, code: e.target.value })} data-testid="invite-code" />
      </div>
      <div className="flex flex-wrap gap-1.5 items-center text-xs text-stone-500">
        <span>They are a</span>
        {["friend", "supporter"].map((k) => <button key={k} className={pill(f.kind === k)} onClick={() => setF({ ...f, kind: k })} data-testid={`invite-kind-${k}`}>{k}</button>)}
        <span className="ml-2">· I share</span>
        {["garden", "habits", "everything"].map((t) => <button key={t} className={pill(f.tier === t)} onClick={() => setF({ ...f, tier: t })} data-testid={`invite-tier-${t}`}>{t}</button>)}
      </div>
      <p className="text-xs text-stone-500">Friends see each other at a tier each of you sets. A supporter only sees you — you see nothing of theirs unless they invite you back.</p>
      <button className="amber-btn text-sm" onClick={send} disabled={!f.handle && !f.code} data-testid="invite-send">Send invite</button>
    </div>
  );
}

function Hides({ c, habits, reload }) {
  const toggle = async (h) => { await api.patch(`/connections/${c.id}/habits/${h.id}/hide`, { hidden: !c.hidden_habits.includes(h.id) }); reload(); };
  if (!habits.length) return null;
  return (
    <div className="flex flex-wrap gap-1.5 mt-2" data-testid={`hides-${c.id}`}>
      {habits.map((h) => { const hid = c.hidden_habits.includes(h.id); return (
        <button key={h.id} onClick={() => toggle(h)} data-testid={`hide-${c.id}-${h.id}`} className={`inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-full ${hid ? "bg-stone-200 text-stone-500 line-through" : "bg-emerald-50 text-emerald-800"}`}>
          {hid ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />} {h.title}</button>); })}
    </div>
  );
}

function ConnectionRow({ c, blurbs, habits, reload }) {
  const act = (fn, ok) => fn().then(() => { if (ok) toast.success(ok); reload(); }).catch((e) => toast.error(e?.response?.data?.detail || "Couldn't save"));
  const mine = c.direction === "i_share";
  return (
    <li className="py-4" data-testid={`connection-${c.id}`} data-status={c.status}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="font-semibold text-stone-900">{c.other.handle} <span className="text-xs font-normal text-stone-500">· {c.kind}{c.coach_view ? " · coach" : ""}{c.status === "pending" ? " · invite waiting" : ""}</span></div>
          <div className="text-xs text-stone-500 mt-0.5">{mine ? `You share: ${c.tier_label}. ${blurbs[c.tier]}` : `They share with you: ${c.tier_label}.`}</div>
        </div>
        <div className="flex gap-1.5 flex-wrap">
          {c.status === "pending" && !mine && ["garden", "habits", "everything"].map((t) => <button key={t} className={pill(false)} onClick={() => act(() => api.post(`/connections/${c.id}/accept`, { tier: t }), "Connected.")} data-testid={`accept-${c.id}-${t}`}><Check className="w-3 h-3 inline" /> Accept, share {t}</button>)}
          {c.status === "active" && !mine && <a href={`/people/${c.id}`} className={pill(true)} data-testid={`view-${c.id}`}>See their garden</a>}
          <button className={pill(false)} onClick={() => act(() => api.post(`/connections/${c.id}/end`), c.status === "pending" ? "Invite declined." : "Connection ended.")} data-testid={`end-${c.id}`}><X className="w-3 h-3 inline" /> {c.status === "pending" ? (mine ? "Cancel" : "Decline") : "End"}</button>
        </div>
      </div>
      {mine && c.status === "active" && (
        <div className="mt-2 flex flex-wrap gap-1.5 items-center">
          {["garden", "habits", "everything"].map((t) => <button key={t} className={pill(c.tier === t)} onClick={() => act(() => api.patch(`/connections/${c.id}/tier`, { tier: t }))} data-testid={`tier-${c.id}-${t}`}>{t}</button>)}
          {c.kind === "supporter" && <button className={`${pill(c.coach_view)} ml-2`} onClick={() => act(() => api.patch(`/connections/${c.id}/coach`, { coach_view: !c.coach_view }), c.coach_view ? "Coach view removed." : "Coach view granted.")} data-testid={`coach-${c.id}`}>{c.coach_view ? "Coach view: on" : "Grant coach view"}</button>}
        </div>
      )}
      {mine && c.status === "active" && c.tier !== "garden" && <Hides c={c} habits={habits} reload={reload} />}
    </li>
  );
}

export default function MyPeople() {
  const [data, setData] = useState(null);
  const [habits, setHabits] = useState([]);
  const load = () => api.get("/connections").then((r) => setData(r.data));
  useEffect(() => { load(); api.get("/habits").then((r) => setHabits(r.data || [])).catch(() => {}); }, []);
  if (!data) return <div className="p-8 text-sm text-stone-500">Loading…</div>;
  const toggleCoach = () => api.patch("/me/coach-tools", { coach_view: !data.coach_tools }).then(load);
  return (
    <div className="max-w-3xl mx-auto px-4 py-8 space-y-6" data-testid="my-people-page">
      <div>
        <h1 className="font-display text-4xl font-bold text-stone-900 flex items-center gap-2"><Users className="w-7 h-7" /> My people</h1>
        <p className="text-sm text-stone-600 mt-1">You decide who sees what. Hiding a habit from someone wins over every tier, recap and suggestion.</p>
        {data.my_code && <p className="text-xs text-stone-500 mt-1">Your connect code: <span className="font-mono font-semibold" data-testid="my-connect-code">{data.my_code}</span></p>}
      </div>
      <InviteForm onDone={load} />
      <section className="warm-card p-5">
        <ul className="divide-y divide-stone-100">
          {data.connections.length === 0 && <li className="py-6 text-sm text-stone-500 text-center" data-testid="no-connections">No connections yet. Your garden is yours alone until you invite someone.</li>}
          {data.connections.map((c) => <ConnectionRow key={c.id} c={c} blurbs={data.tier_blurbs} habits={habits} reload={load} />)}
        </ul>
      </section>
      <section className="warm-card p-5 flex items-start justify-between gap-3 flex-wrap" data-testid="coach-tools-card">
        <div><div className="font-semibold text-stone-900">Coach tools</div><p className="text-xs text-stone-500 mt-0.5 max-w-md">When someone grants you Coach view you get recaps, stage heads-ups and can propose plans — proposals only, never edits. Peer support, not therapy or medical care.</p></div>
        <button className={pill(data.coach_tools)} onClick={toggleCoach} data-testid="coach-tools-toggle">{data.coach_tools ? "Coach tools: on" : "Turn on coach tools"}</button>
      </section>
    </div>
  );
}
